// The viewport's size, for the kit and the shell's tier (APP_SHELL.md). In the
// kit so a screen never imports a component library's hooks to ask it.

import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useState, useSyncExternalStore } from 'react';

/** The window's width; outside a browser (a test's first render), desktop's. */
function windowWidth(): number {
  return typeof window === 'undefined' ? 1200 : window.innerWidth;
}

function onResize(changed: () => void): () => void {
  window.addEventListener('resize', changed);
  window.addEventListener('orientationchange', changed);
  return () => {
    window.removeEventListener('resize', changed);
    window.removeEventListener('orientationchange', changed);
  };
}

/** A phone's window: the phone tier's (`tierFor`). */
export const isPhoneWidth = (width: number): boolean => width <= 600;

/** A window a workshop fills whole, under 1000: a phone's and a tablet's (`Workshop`). */
export const isNarrowWidth = (width: number): boolean => width < 1000;

/**
 * What a screen makes of the window's width — a tier, a phone's breakpoint —
 * re-rendering only when that changes, not at every pixel of a drag. Until
 * 2026-10-26 every reader of the width re-rendered on every resize event,
 * and Players with 400 rows took seconds to follow a window being dragged
 * (B-520). `pick` returns a value compared by identity: a word, a flag.
 *
 * MEASURED ON THE FIRST RENDER, not after it. Mantine's hook reported 0
 * until its effect ran, and the first version of this answered 1200 for that
 * render — so a phone drew one desktop frame first, and anything a screen
 * keeps from its first render kept the desktop's answer: the Matchup opened
 * a phone on head to head instead of its own list (B-209), because the
 * split's first tab is chosen once.
 */
export function useViewportWidth<T extends string | number | boolean>(
  pick: (width: number) => T,
): T {
  return useSyncExternalStore(
    onResize,
    () => pick(windowWidth()),
    () => pick(1200),
  );
}

/** A value that settles a moment after it stops changing: a search as you type. */
export function useSettled<T>(value: T, ms: number): T {
  const [settled] = useDebouncedValue(value, ms);
  return settled;
}

/**
 * Whether the main pointer is a mouse or a trackpad (`pointer: fine`): what a
 * desktop's drag is offered on (B-288). False outside a browser, and on a
 * touch screen, where a drag is a scroll.
 */
export function useFinePointer(): boolean {
  const query = '(pointer: fine)';
  const read = () => typeof window !== 'undefined' && window.matchMedia(query).matches;
  const [fine, setFine] = useState(read);
  useEffect(() => {
    const list = window.matchMedia(query);
    const on = () => setFine(list.matches);
    list.addEventListener('change', on);
    on();
    return () => list.removeEventListener('change', on);
  }, []);
  return fine;
}
